import { useState } from 'react';
import { X, Gavel } from 'lucide-react';
import { Button } from '../../ui/button';
import { purchasingAPI, GoodsReceiptNote } from '../../../lib/api';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../../../utils/errorUtils';
import FileDropZone from '../../common/FileDropZone';

interface BulkAppealModalProps {
  isOpen: boolean;
  onClose: () => void;
  grns: GoodsReceiptNote[];
  onSuccess: () => void;
}

// Mirrors the reason list used by the per-row RaiseAppealModal.
const REASON_CODES = [
  'Delivered on time - see attached POD',
  'Incorrect receipt date recorded',
  'Early delivery agreed with customer',
  'Delivery date changed by customer',
  'Quantity discrepancy',
  'System error',
  'Other',
];

/**
 * Supplier bulk (multi-select) delivery-appeal modal.
 *
 * Raises ONE "delivered on time" appeal per selected delivery in a single
 * submit — each appeal claims its own Original Due Date (ODD) server-side.
 * Reason code, note and attachments are shared across all selected GRNs.
 */
export default function BulkAppealModal({ isOpen, onClose, grns, onSuccess }: BulkAppealModalProps) {
  const [reasonCode, setReasonCode] = useState('');
  const [supplierNote, setSupplierNote] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  const count = grns.length;

  const handleSubmit = async () => {
    if (count === 0) {
      toast.error('No deliveries selected');
      return;
    }
    if (!reasonCode) {
      toast.error('Please select a reason code');
      return;
    }

    try {
      setSubmitting(true);
      const formData = new FormData();
      grns.forEach((grn) => formData.append('grn_ids', String(grn.id)));
      formData.append('reason_code', reasonCode);
      if (supplierNote) formData.append('supplier_note', supplierNote);
      files.forEach((file) => formData.append('files', file));

      const res = await purchasingAPI.bulkCreateDeliveryAppeals(formData);
      const { created_count, skipped_count, skipped } = res.data;

      toast.success(
        skipped_count > 0
          ? `${created_count} appeals raised, ${skipped_count} skipped`
          : `${created_count} appeals raised`,
      );
      if (skipped_count > 0 && skipped.length > 0) {
        const reasons = skipped
          .slice(0, 2)
          .map((s) => `${s.grn_number ?? s.grn_id}: ${s.reason}`)
          .join('; ');
        const suffix = skipped.length > 2 ? ` (+${skipped.length - 2} more)` : '';
        toast.warning(`Skipped — ${reasons}${suffix}`);
      }

      await Promise.resolve(onSuccess());
      onClose();
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Failed to submit appeals'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-[60]" onClick={onClose} />
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          {/* Header. CR-7319: the appeal gavel, not the warning triangle. */}
          <div className="flex items-center justify-between p-5 border-b sticky top-0 bg-white z-10">
            <div className="flex items-center gap-2">
              <Gavel className="h-5 w-5 text-orange-500" />
              <h3 className="text-lg font-bold">Appeal Selected Deliveries</h3>
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                {count} selected
              </span>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Explanation */}
          <div className="px-5 pt-4">
            <p className="text-sm text-gray-600">
              These deliveries will be appealed as delivered on time — each claims its own
              Original Due Date (ODD). The customer reviews each.
            </p>
          </div>

          {/* Selected GRN summary */}
          <div className="px-5 pt-4">
            <p className="text-sm font-semibold text-orange-700 mb-2">Selected deliveries</p>
            <div className="border rounded-lg divide-y max-h-52 overflow-y-auto">
              {grns.map((grn) => (
                <div key={String(grn.id)} className="px-3 py-2 text-sm grid grid-cols-4 gap-2">
                  <span className="font-medium truncate" title={grn.grn_number}>{grn.grn_number}</span>
                  <span className="text-gray-600 truncate" title={grn.po_number}>{grn.po_number || '-'}</span>
                  <span className="font-mono text-gray-600 truncate" title={grn.part_number}>{grn.part_number || '-'}</span>
                  <span className="capitalize text-gray-500">{grn.on_time_status || '-'}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Inputs */}
          <div className="p-5 space-y-4">
            {/* Reason Code */}
            <div>
              <label className="text-sm font-medium block mb-1">
                Reason Code <span className="text-orange-600">*</span>
              </label>
              <select
                value={reasonCode}
                onChange={(e) => setReasonCode(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="">Select a reason...</option>
                {REASON_CODES.map((code) => (
                  <option key={code} value={code}>{code}</option>
                ))}
              </select>
            </div>

            {/* Additional Comments */}
            <div>
              <label className="text-sm font-medium block mb-1">Additional Comments</label>
              <textarea
                value={supplierNote}
                onChange={(e) => setSupplierNote(e.target.value)}
                placeholder="Provide additional details for these appeals..."
                rows={3}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
              />
            </div>

            {/* Attachments */}
            <div>
              <label className="text-sm font-medium block mb-1">Attachments</label>
              {/* Customer review 11 Sep (drag and drop everywhere). */}
              <FileDropZone
                multiple
                size="compact"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.png,.jpg,.jpeg,.gif"
                hint="PDF, Word, Excel, CSV or images"
                onFiles={setFiles}
                data-testid="bulk-appeal-drop-zone"
              />
              {files.length > 0 && (
                <p className="text-xs text-gray-500 mt-1">{files.length} file(s) selected</p>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 p-5 border-t sticky bottom-0 bg-white">
            <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting || count === 0 || !reasonCode}
              className="bg-orange-600 hover:bg-orange-700 text-white"
            >
              {submitting ? 'Submitting...' : `Appeal ${count} Deliver${count === 1 ? 'y' : 'ies'}`}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
